import { Store } from '../../../store/index';
import { REACT_APP_CONTACT_SYNC } from '../../../Components/processENV';
import { getFormatPhoneNumber } from '../../../Helpers/Utility';

export const formatCallDuration = (startTime, durationOrEndTime) => {
    // Explicit check for undefined or null allows `duration: 0` to pass correctly.
    if (durationOrEndTime === undefined || durationOrEndTime === null) return null;

    let diffInSeconds = 0;

    // If durationOrEndTime is a large number (e.g., > 1 year in seconds), it's likely a timestamp
    // 31536000 = 1 year in seconds
    if (durationOrEndTime > 31536000) {
        if (!startTime) return null; // If it's a timestamp, we MUST have a startTime to calculate duration.

        const startMs = toMs(startTime);
        const endMs = toMs(durationOrEndTime);

        if (endMs > startMs) {
            diffInSeconds = Math.floor((endMs - startMs) / 1000);
        }
    } else {
        // It's already an explicit duration passed down
        diffInSeconds = Number(durationOrEndTime);
    }

    if (diffInSeconds <= 0 || isNaN(diffInSeconds)) return null;

    const h = Math.floor(diffInSeconds / 3600);
    const m = Math.floor((diffInSeconds % 3600) / 60);
    const s = Math.floor(diffInSeconds % 60);

    if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Helper to safely convert timestamps to milliseconds
const toMs = timestamp => {
    // If timestamp has 13+ digits, it's mostly likely in milliseconds already (e.g. 1772629065131)
    // If it's 10 digits, it's in seconds (e.g. 1772629058)
    return timestamp > 1000000000000 ? timestamp : timestamp * 1000;
};

export const formatCallDateStr = timestamp => {
    if (!timestamp) return '';
    const d = new Date(toMs(timestamp));
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    let datePart;
    if (d.toDateString() === today.toDateString()) {
        datePart = 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
        datePart = 'Yesterday';
    } else {
        const day = String(d.getDate()).padStart(2, '0');
        const month = d.toLocaleString('en-US', { month: 'short' });
        datePart = `${day} ${month}`;
    }

    let hours = d.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours || 12; // the hour '0' should be '12'
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${datePart}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
};

export const getCallGroupLabel = timestamp => {
    if (!timestamp) return 'Older';
    const d = new Date(toMs(timestamp));
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';

    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month}, ${d.getFullYear()}`;
};

export const transformAndGroupLogs = apiLogs => {
    if (!apiLogs || !Array.isArray(apiLogs)) return [];

    const groupsMap = new Map();
    const usersMap = Store.getState().userInfoData.users;
    const rosterData = Store.getState().rosterData?.data || [];
    const rosterMap = Object.fromEntries(rosterData.map(r => [r.userId, r]));

    const getIdFromJid = jid => {
        if (!jid) return jid;
        return jid.split('@')[0];
    };

    apiLogs.forEach(log => {
        const ts = log.startTime || log.callTime;
        const groupLabel = getCallGroupLabel(ts);

        if (!groupsMap.has(groupLabel)) {
            groupsMap.set(groupLabel, { label: groupLabel, logs: [] });
        }
        const group = groupsMap.get(groupLabel);

        const participants = (log.callParticipants || []).map(p => {
            const rawIdentifier = p.username || p.userId;
            const userIdentifier = getIdFromJid(rawIdentifier);
            // Fetch matching user from redux profile store, or use nickname passed from SDK if available
            const storeUser = usersMap[userIdentifier] || {};
            let rosterUser = rosterMap[userIdentifier] || rosterMap[rawIdentifier];
            if (!rosterUser && userIdentifier) {
                const userPhone = (storeUser.userId || storeUser.username || storeUser.mobileNumber || '').split(
                    '@',
                )[0];
                const searchId = userPhone && userPhone !== userIdentifier ? userPhone : userIdentifier;

                rosterUser = rosterData.find(r => {
                    if (!r) return false;
                    const rid = r.userId?.split('@')[0];
                    if (!rid || rid.length < 7 || searchId.length < 7) return false;
                    return rid.endsWith(searchId) || searchId.endsWith(rid);
                });
            }
            rosterUser = rosterUser || {};
            const isFriend = storeUser.isFriend || rosterUser.isFriend;
            const isDeletedUser = storeUser.isDeletedUser || rosterUser.isDeletedUser;

            // Check if nickname is a raw base64 encrypted string (common 24-char AES strings ends with ==)
            const isBase64 =
                p.nickname &&
                /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(p.nickname) &&
                p.nickname.length >= 16 &&
                !p.nickname.includes(' ');
            const safeNickname = isBase64 ? '' : p.nickname;

            // User nickname formatting: Roster name > Roster displayName > Profile nickName
            const displayName =
                REACT_APP_CONTACT_SYNC && !isFriend && !isDeletedUser
                    ? getFormatPhoneNumber(userIdentifier) || userIdentifier
                    : rosterUser?.name ||
                      rosterUser?.displayName ||
                      rosterUser?.nickName ||
                      storeUser?.nickName ||
                      storeUser?.name ||
                      safeNickname ||
                      userIdentifier ||
                      'Unknown';
            const avatarUrl =
                storeUser?.image || storeUser?.thumbImage || storeUser?.profileImage || p.thumbimage || p.image || '';
            const colorCode = storeUser?.colorCode || '';
            return {
                userId: userIdentifier,
                name: displayName,
                avatarUrl: avatarUrl,
                colorCode: colorCode,
            };
        });

        const isMissedStatus = typeof log.missedStatus === 'number' ? log.missedStatus > 0 : !!log.missedStatus;
        const isTimeout =
            (typeof log.status === 'string' && /time-?out/i.test(log.status)) ||
            (typeof log.callerStatus === 'string' && /time-?out/i.test(log.callerStatus)) ||
            (typeof log.callStatus === 'string' && /time-?out/i.test(log.callStatus)) ||
            (typeof log.callState === 'string' && /time-?out/i.test(log.callState)) ||
            log.callState === 8;

        const isZeroStart = log.startTime === 0 || log.startTime === '0';

        const isMissed = isMissedStatus || isTimeout || isZeroStart;

        const durationValue = log.duration !== undefined && log.duration !== null ? log.duration : log.endTime;
        const formattedDuration = isMissed ? null : formatCallDuration(log.startTime, durationValue);

        group.logs.push({
            id: (log.callId || log.id || log.call_id || log.startTime || log.callTime || Math.random())?.toString(),
            participants: participants,
            groupName: log.groupName,
            isMissed: isMissed,
            isIncoming: typeof log.isCaller === 'number' ? log.isCaller === 0 : log.isCaller === false,
            isVideo: log.callType === 'VIDEO',
            dateStr: formatCallDateStr(ts),
            callTime: ts,
            groupId: log.chatId,
            duration: formattedDuration,
        });
    });

    return Array.from(groupsMap.values());
};

export const getFormattedNames = (participants = []) => {
    if (!participants || participants.length === 0) return '';
    const names = participants.map(p => p.name || p.userId);
    if (names.length <= 2) return names.join(', ');
    return names.slice(0, 2).join(', ') + `, +${names.length - 2} OTHERS`;
};
